<script>
/**
 * DDukDoc 예약 패널 — 대화 사이드바에서 챗봇 예약 신청을 승인·거절하고 그날 예약 현황을 본다.
 *
 * 데이터·인증은 초안 자동완성(ReplyBox)과 같은 창구 규약이다: `window.ddukdocDraftApiBase` +
 * `window.ddukdocConnectorId`, 상담원 세션 세 헤더. 서버가 Chatwoot에 되물어 상담원을 확인하고
 * 병원은 connector로 되짚는다. 승인·거절은 상담원 이메일이 병원 직원 계정과 맞아야 한다.
 */
import {
  ddukdocAuthHeaders,
  ddukdocReservationsEndpoint,
  isDdukdocReservationsEnabled,
} from 'dashboard/api/hospital/ddukdocReservations';
import DatePicker from 'vue-datepicker-next';
import {
  pickDefaultTreatmentId,
  shiftDate,
  describeDecisionResult,
  statusLabelKey,
} from './helpers/ddukdocReservationHelper';

export default {
  name: 'DdukdocReservationPanel',
  components: { DatePicker },
  props: {
    conversationId: { type: [Number, String], required: true },
  },
  data() {
    return {
      loading: false,
      loadError: false,
      enabled: isDdukdocReservationsEnabled(),
      requests: [],
      treatments: [],
      day: { date: '', entries: [], emrUnavailable: false },
      canDecide: false,
      selectedTreatment: {},
      rejectOpen: {},
      rejectReason: {},
      rejectAlternative: {},
      busyId: '',
      notice: '',
      noticeTone: 'info',
      requestSeq: 0,
      calendarOpen: false,
      calendarLang: {
        days: ['일', '월', '화', '수', '목', '금', '토'],
        yearFormat: 'YYYY',
        monthFormat: 'M월',
      },
    };
  },
  computed: {
    hasRequests() {
      return this.requests.length > 0;
    },
    // "9월 5일 (금)" — 헤더 pill 표기. 원본 YYYY-MM-DD 는 툴팁에 남긴다.
    dayLabel() {
      const d = this.dayDateValue;
      if (!d) return this.day.date;
      const weekday = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
      return `${d.getMonth() + 1}월 ${d.getDate()}일 (${weekday})`;
    },
    // 상태별 건수 — 확정 / 승인 대기 / 취소·노쇼.
    dayCounts() {
      return this.day.entries.reduce(
        (counts, entry) => {
          const tone = this.statusTone(entry);
          if (tone === 'amber') counts.requested += 1;
          else if (tone === 'muted') counts.cancelled += 1;
          else counts.confirmed += 1;
          return counts;
        },
        { confirmed: 0, requested: 0, cancelled: 0 }
      );
    },
    // 같은 시각 예약을 한 줄로 묶어 시간 레일을 만든다.
    entryGroups() {
      return this.day.entries.reduce((groups, entry) => {
        const last = groups[groups.length - 1];
        if (last && last.time === entry.time) last.entries.push(entry);
        else groups.push({ time: entry.time, entries: [entry] });
        return groups;
      }, []);
    },
    // 예약 현황 날짜(YYYY-MM-DD)를 달력 값(Date)으로. 시간대 오차를 막으려 로컬 자정으로 만든다.
    dayDateValue() {
      const [y, m, d] = String(this.day.date || '')
        .split('-')
        .map(Number);
      if (!y || !m || !d) return null;
      return new Date(y, m - 1, d);
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.notice = '';
        this.load();
      },
    },
  },
  methods: {
    authHeaders() {
      return ddukdocAuthHeaders();
    },
    endpoint(query) {
      return ddukdocReservationsEndpoint(query);
    },
    async load(date) {
      if (!this.enabled || !this.conversationId) return;
      const headers = this.authHeaders();
      if (!headers) return;
      this.requestSeq += 1;
      const seq = this.requestSeq;
      this.loading = true;
      this.loadError = false;
      try {
        const query = { conversationId: String(this.conversationId) };
        if (date) query.date = date;
        const res = await fetch(this.endpoint(query), { headers });
        if (seq !== this.requestSeq) return;
        if (!res.ok) {
          this.loadError = true;
          return;
        }
        const data = await res.json();
        if (seq !== this.requestSeq) return;
        this.requests = Array.isArray(data.requests) ? data.requests : [];
        this.treatments = Array.isArray(data.treatments) ? data.treatments : [];
        this.day = data.day || { date: '', entries: [], emrUnavailable: false };
        this.canDecide = Boolean(data.agent && data.agent.canDecide);
        const selected = {};
        this.requests.forEach(r => {
          selected[r.id] =
            this.selectedTreatment[r.id] ||
            pickDefaultTreatmentId(r, this.treatments);
        });
        this.selectedTreatment = selected;
      } catch (error) {
        if (seq === this.requestSeq) this.loadError = true;
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    moveDay(delta) {
      this.load(shiftDate(this.day.date, delta));
    },
    toggleCalendar() {
      if (this.loading) return;
      this.calendarOpen = !this.calendarOpen;
    },
    // 달력에서 날짜를 고르면 그날 예약 현황으로 이동한다.
    pickDay(date) {
      this.calendarOpen = false;
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
      const pad = n => String(n).padStart(2, '0');
      const next = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      if (next !== this.day.date) this.load(next);
    },
    async decide(request, action) {
      if (this.busyId) return;
      const headers = this.authHeaders();
      if (!headers) return;
      this.busyId = request.id;
      this.notice = '';
      let result = null;
      try {
        const res = await fetch(this.endpoint({}), {
          method: 'POST',
          headers: { ...headers, 'content-type': 'application/json' },
          body: JSON.stringify({
            conversationId: String(this.conversationId),
            action,
            appointmentId: request.id,
            treatmentId: this.selectedTreatment[request.id] || '',
            reason: this.rejectReason[request.id] || '',
            alternative: this.rejectAlternative[request.id] || '',
          }),
        });
        result = await res.json().catch(() => ({ ok: false }));
        if (!res.ok && result && result.ok === undefined) result.ok = false;
      } catch (error) {
        result = { ok: false };
      } finally {
        this.busyId = '';
      }
      this.notice = describeDecisionResult(result, this.$t);
      this.noticeTone = result && result.ok ? 'success' : 'error';
      if (result && result.ok) {
        this.rejectOpen = { ...this.rejectOpen, [request.id]: false };
        // 결정된 신청은 바로 목록에서 빠져야 한다 — 두 번 누르지 않게.
        this.requests = this.requests.filter(r => r.id !== request.id);
        this.load(this.day.date);
      }
    },
    toggleReject(request) {
      this.rejectOpen = {
        ...this.rejectOpen,
        [request.id]: !this.rejectOpen[request.id],
      };
    },
    statusLabel(entry) {
      return this.$t(statusLabelKey(entry.status));
    },
    // 상태 → 배지 톤. 확정(teal) / 승인 대기(amber) / 취소·노쇼(muted) / 완료(slate).
    statusTone(entry) {
      const key = statusLabelKey(entry.status);
      if (key.endsWith('STATUS_REQUESTED')) return 'amber';
      if (key.endsWith('STATUS_CANCELLED') || key.endsWith('STATUS_NO_SHOW'))
        return 'muted';
      if (key.endsWith('STATUS_DONE')) return 'slate';
      return 'teal';
    },
    statusBadgeClass(entry) {
      const tone = this.statusTone(entry);
      if (tone === 'amber') return 'bg-n-amber-3 text-n-amber-11';
      if (tone === 'muted') return 'bg-n-slate-3 text-n-slate-10';
      if (tone === 'slate') return 'bg-n-slate-3 text-n-slate-11';
      return 'bg-n-teal-3 text-n-teal-11';
    },
    isInactiveEntry(entry) {
      return this.statusTone(entry) === 'muted';
    },
  },
};
</script>

<template>
  <div v-if="enabled" class="ddukdoc-reservations text-sm">
    <p v-if="loadError" class="text-n-ruby-11 mb-2">
      {{ $t('HOSPITAL.RESERVATIONS.LOAD_ERROR') }}
    </p>

    <section class="mb-4">
      <div class="mb-2 flex items-center justify-between">
        <h4 class="text-xs font-semibold tracking-wide text-n-slate-11">
          {{ $t('HOSPITAL.RESERVATIONS.REQUESTS_TITLE') }}
        </h4>
        <span
          v-if="hasRequests"
          class="rounded-full bg-n-amber-3 px-2 py-0.5 text-[11px] font-semibold text-n-amber-11"
        >
          {{ requests.length }}
        </span>
      </div>
      <p
        v-if="!hasRequests && !loading"
        class="rounded-xl bg-n-alpha-1 px-3 py-2.5 text-xs text-n-slate-11"
      >
        {{ $t('HOSPITAL.RESERVATIONS.NO_REQUESTS') }}
      </p>
      <p
        v-if="hasRequests && !canDecide"
        class="mb-2 rounded-xl bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
      >
        {{ $t('HOSPITAL.RESERVATIONS.CANNOT_DECIDE') }}
      </p>
      <div
        v-for="request in requests"
        :key="request.id"
        class="mb-2 rounded-2xl border border-n-weak bg-n-solid-1 p-3 shadow-sm"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="text-sm font-semibold text-n-slate-12">
              {{ request.scheduledAt }}
            </div>
            <div
              v-if="request.procedureHint"
              class="mt-0.5 text-xs text-n-slate-11"
            >
              {{ $t('HOSPITAL.RESERVATIONS.WANTED') }} ·
              {{ request.procedureHint }}
            </div>
          </div>
          <span
            class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium"
            :class="
              request.kind === 'reschedule'
                ? 'bg-n-iris-3 text-n-iris-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            {{
              request.kind === 'reschedule'
                ? $t('HOSPITAL.RESERVATIONS.KIND_RESCHEDULE')
                : $t('HOSPITAL.RESERVATIONS.KIND_CREATE')
            }}
          </span>
        </div>

        <label class="mt-3 block text-xs text-n-slate-11">
          {{ $t('HOSPITAL.RESERVATIONS.TREATMENT') }}
          <select
            v-model="selectedTreatment[request.id]"
            class="mt-1 w-full rounded-xl border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :disabled="!canDecide || busyId === request.id"
          >
            <option v-for="t in treatments" :key="t.id" :value="t.id">
              {{ t.name
              }}<template v-if="t.category"> · {{ t.category }}</template>
            </option>
          </select>
        </label>

        <div class="mt-3 flex gap-2">
          <button
            type="button"
            class="flex-1 rounded-full bg-n-brand px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            :disabled="
              !canDecide ||
              busyId === request.id ||
              !selectedTreatment[request.id]
            "
            @click="decide(request, 'approve')"
          >
            {{
              busyId === request.id
                ? $t('HOSPITAL.RESERVATIONS.WORKING')
                : $t('HOSPITAL.RESERVATIONS.APPROVE')
            }}
          </button>
          <button
            type="button"
            class="flex-1 rounded-full border border-n-weak bg-n-background px-3 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
            :disabled="!canDecide || busyId === request.id"
            @click="toggleReject(request)"
          >
            {{ $t('HOSPITAL.RESERVATIONS.REJECT') }}
          </button>
        </div>

        <div
          v-if="rejectOpen[request.id]"
          class="mt-3 space-y-2 rounded-xl bg-n-alpha-1 p-2.5"
        >
          <textarea
            v-model="rejectReason[request.id]"
            rows="2"
            class="w-full rounded-xl border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-brand"
            :placeholder="$t('HOSPITAL.RESERVATIONS.REASON_PLACEHOLDER')"
          />
          <input
            v-model="rejectAlternative[request.id]"
            type="text"
            class="w-full rounded-xl border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-brand"
            :placeholder="$t('HOSPITAL.RESERVATIONS.ALTERNATIVE_PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('HOSPITAL.RESERVATIONS.REJECT_HINT') }}
          </p>
          <button
            type="button"
            class="w-full rounded-full bg-n-ruby-9 px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            :disabled="busyId === request.id"
            @click="decide(request, 'reject')"
          >
            {{
              busyId === request.id
                ? $t('HOSPITAL.RESERVATIONS.WORKING')
                : $t('HOSPITAL.RESERVATIONS.REJECT_CONFIRM')
            }}
          </button>
        </div>
      </div>
      <p
        v-if="notice"
        class="mt-1 rounded-xl px-3 py-2 text-xs"
        :class="
          noticeTone === 'success'
            ? 'bg-n-teal-2 text-n-teal-11'
            : 'bg-n-ruby-2 text-n-ruby-11'
        "
      >
        {{ notice }}
      </p>
    </section>

    <section>
      <div
        class="mb-2 flex items-center justify-between rounded-full bg-n-alpha-1 p-1"
      >
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-background hover:text-n-slate-12 disabled:opacity-40"
          :disabled="loading"
          :aria-label="$t('HOSPITAL.RESERVATIONS.PREV_DAY')"
          @click="moveDay(-1)"
        >
          <span class="i-lucide-chevron-left size-4" />
        </button>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-n-slate-12 hover:bg-n-background disabled:opacity-40"
          :disabled="loading"
          :aria-expanded="calendarOpen"
          :title="`${$t('HOSPITAL.RESERVATIONS.PICK_DAY')} · ${day.date}`"
          @click="toggleCalendar"
        >
          <span class="i-lucide-calendar-days size-3.5 text-n-slate-10" />
          {{ dayLabel }}
          <span
            v-if="day.entries.length"
            class="rounded-full bg-n-background px-1.5 py-px text-[10px] font-semibold text-n-slate-11"
          >
            {{ day.entries.length }}
          </span>
        </button>
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-background hover:text-n-slate-12 disabled:opacity-40"
          :disabled="loading"
          :aria-label="$t('HOSPITAL.RESERVATIONS.NEXT_DAY')"
          @click="moveDay(1)"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
      </div>
      <!-- vue-datepicker-next 1.0.3에는 inline 모드가 없다. 팝업을 열어 둔 채 정적 배치로 패널 안에 그리고 입력창은 숨긴다. -->
      <div
        v-if="calendarOpen"
        class="mb-2 flex justify-center rounded-2xl border border-n-weak bg-n-solid-1 p-1 [&_.mx-input-wrapper]:hidden [&_.mx-datepicker]:w-auto"
      >
        <DatePicker
          :value="dayDateValue"
          type="date"
          open
          :append-to-body="false"
          :popup-style="{ position: 'static', boxShadow: 'none', margin: 0 }"
          :lang="calendarLang"
          @change="pickDay"
        />
      </div>
      <p
        v-if="day.emrUnavailable"
        class="mb-2 rounded-xl bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
      >
        {{ $t('HOSPITAL.RESERVATIONS.EMR_UNAVAILABLE') }}
      </p>
      <div
        v-if="day.entries.length"
        class="mb-2 flex flex-wrap gap-1.5 text-[11px]"
      >
        <span
          class="rounded-full bg-n-teal-3 px-2 py-0.5 font-medium text-n-teal-11"
        >
          {{ $t('HOSPITAL.RESERVATIONS.STATUS_CONFIRMED') }}
          {{ dayCounts.confirmed }}
        </span>
        <span
          v-if="dayCounts.requested"
          class="rounded-full bg-n-amber-3 px-2 py-0.5 font-medium text-n-amber-11"
        >
          {{ $t('HOSPITAL.RESERVATIONS.STATUS_REQUESTED') }}
          {{ dayCounts.requested }}
        </span>
        <span
          v-if="dayCounts.cancelled"
          class="rounded-full bg-n-slate-3 px-2 py-0.5 font-medium text-n-slate-10"
        >
          {{ $t('HOSPITAL.RESERVATIONS.STATUS_CANCELLED') }}
          {{ dayCounts.cancelled }}
        </span>
      </div>
      <p
        v-if="!loading && day.entries.length === 0"
        class="rounded-xl bg-n-alpha-1 px-3 py-4 text-center text-xs text-n-slate-11"
      >
        {{ $t('HOSPITAL.RESERVATIONS.NO_ENTRIES') }}
      </p>
      <ol v-else class="space-y-1.5">
        <li
          v-for="group in entryGroups"
          :key="group.time"
          class="flex gap-2.5 rounded-2xl border border-n-weak bg-n-solid-1 px-3 py-2"
        >
          <div class="flex w-11 shrink-0 flex-col items-center pt-0.5">
            <span class="text-xs font-semibold tabular-nums text-n-slate-12">
              {{ group.time }}
            </span>
            <span class="mt-1 h-full w-px min-h-3 rounded-full bg-n-weak" />
          </div>
          <ul class="min-w-0 flex-1 space-y-1.5">
            <li
              v-for="(entry, index) in group.entries"
              :key="`${entry.time}-${index}`"
              class="flex items-center gap-2"
            >
              <div class="min-w-0 flex-1">
                <div
                  class="truncate text-sm font-medium text-n-slate-12"
                  :class="{
                    'line-through text-n-slate-10': isInactiveEntry(entry),
                  }"
                >
                  {{
                    entry.patientName ||
                    $t('HOSPITAL.RESERVATIONS.UNKNOWN_PATIENT')
                  }}
                </div>
                <div
                  v-if="entry.procedureName"
                  class="truncate text-xs text-n-slate-11"
                >
                  {{ entry.procedureName }}
                </div>
              </div>
              <span
                class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium"
                :class="statusBadgeClass(entry)"
              >
                {{ statusLabel(entry) }}
              </span>
            </li>
          </ul>
        </li>
      </ol>
    </section>
  </div>
</template>
